<template>
  <div style="max-width: 1400px; margin: auto" class="pa-3">
    <!-- Header -->
    <v-toolbar dark flat color="transparent" class="px-0 mb-3">
      <v-toolbar-title>
        <v-icon left>mdi-medical-bag</v-icon>
        Unfallmeldungen
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed rounded @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        <span class="d-none d-sm-inline">Neue Meldung</span>
      </v-btn>
    </v-toolbar>

    <!-- Filter -->
    <v-card dark class="mb-3 pa-3" color="rgba(255,255,255,0.05)" flat>
      <v-row dense>
        <v-col v-if="canManage" cols="12" sm="6" md="4">
          <v-select
            v-model="filterStudio"
            :items="studioFilterOptions"
            item-text="name"
            item-value="_id"
            label="Studio"
            outlined
            dense
            dark
            hide-details
            @change="loadReports" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select v-model="filterStatus" :items="statusFilterOptions" label="Status" outlined dense dark hide-details @change="loadReports" />
        </v-col>
      </v-row>
    </v-card>

    <!-- Liste -->
    <v-card dark color="rgba(255,255,255,0.03)" flat>
      <v-data-table
        dark
        :headers="tableHeaders"
        :items="reports"
        :loading="loading"
        :items-per-page="25"
        class="transparent"
        no-data-text="Keine Unfallmeldungen vorhanden"
        @click:row="openDetailDialog">
        <template v-slot:item.accidentDate="{ item }">
          {{ $moment(item.accidentDate).format("DD.MM.YYYY HH:mm") }}
        </template>
        <template v-slot:item.name="{ item }"> {{ item.firstName }} {{ item.lastName }} </template>
        <template v-slot:item.studio="{ item }">
          {{ item.studio ? item.studio.name : "-" }}
        </template>
        <template v-slot:item.isWorkAccident="{ item }">
          <v-chip x-small :color="item.isWorkAccident ? 'orange' : 'grey'" dark>
            {{ item.isWorkAccident ? "Betrieb" : "Privat" }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click.stop="downloadPdf(item)">
            <v-icon small>mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn icon small @click.stop="openDetailDialog(item)">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- CREATE DIALOG -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="createDialog" max-width="800" persistent scrollable>
      <v-card dark>
        <v-card-title>
          <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus-circle" }}</v-icon>
          {{ isEditMode ? "Unfallmeldung bearbeiten" : "Neue Unfallmeldung" }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <v-form ref="createForm" v-model="formValid" class="pt-3">
            <h3 class="mb-3">Persönliche Angaben</h3>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.studio"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Anstellungsort *"
                  outlined
                  dense
                  dark
                  :disabled="!canManage && !isEditMode"
                  :hint="!canManage ? 'Dein angemeldetes Studio' : ''"
                  persistent-hint
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.entryDate" label="Eintrittsdatum" type="date" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.firstName" label="Vorname *" outlined dense dark :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.lastName" label="Nachname *" outlined dense dark :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.address" label="Adresse" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.phone" label="Telefon" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.email" label="E-Mail" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.birthday" label="Geburtstag" type="date" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <h3 class="mb-3">Angaben zum Unfall</h3>
            <v-row dense>
              <v-col cols="12">
                <v-switch v-model="form.isWorkAccident" label="Betriebsunfall (während Arbeit oder Arbeitsweg)" dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.accidentDate"
                  label="Unfalldatum & Zeit *"
                  type="datetime-local"
                  outlined
                  dense
                  dark
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.accidentLocation"
                  label="Unfallort und Stelle"
                  hint="Ortschaft angeben!"
                  placeholder="z.B. Frauenfeld, Fitnesscenter"
                  outlined
                  dense
                  dark />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.accidentDescription"
                  label="Was genau ist passiert? *"
                  outlined
                  dense
                  dark
                  rows="3"
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="form.hasPoliceReport" label="Polizeirapport vorhanden" dark />
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <h3 class="mb-3">Verletzung</h3>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.injuredBodyPart" label="Was ist verletzt?" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.bodySide"
                  :items="[
                    { text: 'Nicht zutreffend', value: 'n/a' },
                    { text: 'Unbestimmt', value: 'unbestimmt' },
                    { text: 'Links', value: 'links' },
                    { text: 'Rechts', value: 'rechts' },
                    { text: 'Beide', value: 'beide' },
                  ]"
                  label="Körperseite"
                  outlined
                  dense
                  dark />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.injuryType" label="Art der Verletzung" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <h3 class="mb-3">Ärztliche Behandlung</h3>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.firstDoctor" label="Erstbehandelnder Arzt" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.followUpDoctor" label="Nachbehandelnder Arzt" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <h3 class="mb-3">Arbeitsausfall</h3>
            <v-row dense>
              <v-col cols="12">
                <v-switch v-model="form.absenceMoreThan3Days" label="Arbeit mehr als 3 Tage ausgesetzt" dark />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="form.absenceMoreThan30Days" label="Arbeit voraussichtlich mehr als 30 Tage ausgesetzt" dark />
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <h3 class="mb-3">Arbeitsunfähigkeitszeugnis</h3>

            <v-alert dense text type="info" color="primary" class="mb-3" style="font-size: 13px">
              Bitte lade das Arbeitsunfähigkeitszeugnis direkt hier hoch. Falls dir noch keines vorliegt, sende es nachträglich an
              <strong>ja@wellcomefit.ch</strong>.
            </v-alert>

            <!-- Bestehende Anhänge (Edit-Modus) -->
            <div v-if="isEditMode && existingAttachments.length > 0" class="mb-3">
              <div class="caption grey--text mb-2">Bereits hochgeladen</div>
              <v-card v-for="att in existingAttachments" :key="att.fileId" flat color="rgba(255,255,255,0.05)" class="pa-2 mb-2 d-flex align-center">
                <v-icon left small>mdi-paperclip</v-icon>
                <span class="text-truncate" style="flex: 1; font-size: 13px">{{ att.originalName }}</span>
                <span class="caption grey--text ml-2">{{ formatFileSize(att.size) }}</span>
                <v-btn icon small @click="markAttachmentForRemoval(att.fileId)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </v-card>
            </div>

            <v-file-input
              v-model="form.newAttachments"
              multiple
              accept=".pdf,image/*"
              label="Datei(en) hochladen"
              outlined
              dense
              dark
              show-size
              counter
              prepend-icon=""
              prepend-inner-icon="mdi-file-upload"
              hint="PDF oder Bild (JPG/PNG). Max. 15 MB pro Datei."
              persistent-hint />
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog" :disabled="saving">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="!formValid" @click="saveReport">
            {{ isEditMode ? "Speichern" : "Erfassen & an HR senden" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DETAIL DIALOG -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="detailDialog" max-width="900" scrollable>
      <v-card dark v-if="currentReport">
        <v-card-title>
          <v-icon left>mdi-file-document</v-icon>
          Unfallmeldung: {{ currentReport.firstName }} {{ currentReport.lastName }}
          <v-spacer></v-spacer>
          <v-chip small :color="statusColor(currentReport.status)" dark class="mr-2">
            {{ statusLabel(currentReport.status) }}
          </v-chip>
          <!-- NEU: Edit-Button, nur wenn bearbeitbar -->
          <v-btn icon v-if="canEdit(currentReport)" @click="openEditDialog(currentReport)" title="Bearbeiten">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <h4 class="mb-2">Persönliche Angaben</h4>
              <detail-row label="Studio" :value="currentReport.studio && currentReport.studio.name" />
              <detail-row label="MA" :value="currentReport.firstName + ' ' + currentReport.lastName" />
              <detail-row label="Adresse" :value="currentReport.address" />
              <detail-row label="Telefon" :value="currentReport.phone" />
              <detail-row label="E-Mail" :value="currentReport.email" />
              <detail-row label="Geburtstag" :value="formatDate(currentReport.birthday)" />
              <detail-row label="Eintrittsdatum" :value="formatDate(currentReport.entryDate)" />
            </v-col>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Unfall</h4>
              <detail-row label="Betriebsunfall" :value="currentReport.isWorkAccident ? 'JA' : 'NEIN'" />
              <detail-row label="Datum/Zeit" :value="formatDateTime(currentReport.accidentDate)" />
              <detail-row label="Ort" :value="currentReport.accidentLocation" />
              <detail-row label="Polizeirapport" :value="currentReport.hasPoliceReport ? 'JA' : 'NEIN'" />
              <detail-row label="Verletzung" :value="currentReport.injuredBodyPart" />
              <detail-row label="Körperseite" :value="currentReport.bodySide" />
              <detail-row label="Art" :value="currentReport.injuryType" />
            </v-col>
            <v-col cols="12">
              <h4 class="mb-2">Beschreibung</h4>
              <p class="grey--text text--lighten-2" style="white-space: pre-wrap">{{ currentReport.accidentDescription || "-" }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Ärzte</h4>
              <detail-row label="Erstbehandlung" :value="currentReport.firstDoctor" />
              <detail-row label="Nachbehandlung" :value="currentReport.followUpDoctor" />
            </v-col>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Arbeitsausfall</h4>
              <detail-row label=">3 Tage" :value="currentReport.absenceMoreThan3Days ? 'JA' : 'NEIN'" />
              <detail-row label=">30 Tage" :value="currentReport.absenceMoreThan30Days ? 'JA' : 'NEIN'" />
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Status ändern (nur mit Manage-Recht) -->
          <div v-if="canManage" class="mb-4">
            <h4 class="mb-2">Status verwalten</h4>
            <v-btn-toggle v-model="currentReport.status" mandatory dense @change="changeStatus">
              <v-btn small value="open">Offen</v-btn>
              <v-btn small value="in_progress">In Bearbeitung</v-btn>
              <v-btn small value="done">Erledigt</v-btn>
            </v-btn-toggle>
          </div>
          <v-divider class="my-4" v-if="currentReport.attachments && currentReport.attachments.length > 0"></v-divider>

          <div v-if="currentReport.attachments && currentReport.attachments.length > 0">
            <h4 class="mb-2">
              <v-icon left small>mdi-paperclip</v-icon>
              Anhänge ({{ currentReport.attachments.length }})
            </h4>
            <v-card
              v-for="att in currentReport.attachments"
              :key="att.fileId"
              flat
              color="rgba(255,255,255,0.05)"
              class="pa-2 mb-2 d-flex align-center">
              <v-icon left small>{{ att.mimeType && att.mimeType.startsWith("image/") ? "mdi-image" : "mdi-file-pdf-box" }}</v-icon>
              <div style="flex: 1; min-width: 0">
                <div class="text-truncate" style="font-size: 13px">{{ att.originalName }}</div>
                <div class="caption grey--text">
                  {{ formatFileSize(att.size) }} ·
                  {{ formatDateTime(att.uploadedAt) }}
                </div>
              </div>
              <v-btn icon small @click="previewAttachment(att)" title="Vorschau">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn icon small @click="downloadAttachmentFile(att)" title="Herunterladen">
                <v-icon small>mdi-download</v-icon>
              </v-btn>
              <v-btn v-if="canEdit(currentReport)" icon small color="error" @click="deleteAttachmentFile(att)" title="Löschen">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-card>
          </div>
          <v-divider class="my-4" v-if="currentReport.editHistory && currentReport.editHistory.length > 0"></v-divider>

          <div v-if="currentReport.editHistory && currentReport.editHistory.length > 0">
            <h4 class="mb-2">
              <v-icon left small>mdi-history</v-icon>
              Bearbeitungshistorie ({{ currentReport.editHistory.length }})
            </h4>
            <v-expansion-panels dark flat>
              <v-expansion-panel
                v-for="(h, idx) in [...currentReport.editHistory].reverse()"
                :key="idx"
                style="background: rgba(255, 255, 255, 0.03)">
                <v-expansion-panel-header>
                  <div>
                    <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                    <span class="caption grey--text ml-2">{{ formatDateTime(h.editedAt) }}</span>
                    <v-chip x-small class="ml-2" color="blue-grey" dark> {{ h.changes ? h.changes.length : 0 }} Änderungen </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(c, cidx) in h.changes" :key="cidx" class="mb-2" style="font-size: 13px">
                    <strong>{{ c.field }}:</strong>
                    <span style="color: #c0392b">{{ formatHistoryValue(c.oldValue, c.field) }}</span>
                    →
                    <span style="color: #27ae60">{{ formatHistoryValue(c.newValue, c.field) }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          <v-divider class="my-4"></v-divider>

          <!-- Kommentare -->
          <h4 class="mb-2">
            <v-icon left small>mdi-comment-multiple</v-icon>
            Kommentare ({{ currentReport.comments ? currentReport.comments.length : 0 }})
          </h4>
          <div v-if="currentReport.comments && currentReport.comments.length > 0">
            <v-card v-for="(c, idx) in currentReport.comments" :key="idx" class="mb-2 pa-3" color="rgba(255,255,255,0.05)" flat>
              <div class="d-flex justify-space-between align-center mb-1">
                <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
                <span class="caption grey--text">{{ formatDateTime(c.createdAt) }}</span>
              </div>
              <div style="white-space: pre-wrap">{{ c.comment }}</div>
            </v-card>
          </div>
          <p v-else class="grey--text">Noch keine Kommentare.</p>

          <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="mt-3" hide-details />
          <div class="text-right mt-2">
            <v-btn small color="primary" :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
              <v-icon left small>mdi-send</v-icon>Kommentar senden
            </v-btn>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text color="error" v-if="canManage" @click="deleteReport"> <v-icon left>mdi-delete</v-icon>Löschen </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="downloadPdf(currentReport)"> <v-icon left>mdi-file-pdf-box</v-icon>PDF </v-btn>
          <v-btn text @click="detailDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"

const DetailRow = {
  props: ["label", "value"],
  render(h) {
    return h("div", { class: "d-flex mb-1", style: "font-size: 13px;" }, [
      h("span", { class: "grey--text", style: "min-width: 140px;" }, this.label + ":"),
      h("span", this.value || "-"),
    ])
  },
}

export default {
  name: "AccidentReports",
  components: {
    // Kleine Inline-Komponente für Detail-Zeilen
    "detail-row": DetailRow,
  },
  data() {
    return {
      userService,
      loading: false,
      saving: false,
      savingComment: false,
      reports: [],
      filterStudio: "all",
      filterStatus: "open",
      editingId: null,
      createDialog: false,
      detailDialog: false,
      formValid: false,
      currentReport: null,
      newComment: "",
      form: this.getEmptyForm(),
      existingAttachments: [],
      tableHeaders: [
        { text: "Datum", value: "accidentDate" },
        { text: "Name", value: "name" },
        { text: "Studio", value: "studio" },
        { text: "Typ", value: "isWorkAccident" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      statusFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Offen", value: "open" },
        { text: "In Bearbeitung", value: "in_progress" },
        { text: "Erledigt", value: "done" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    isEditMode() {
      return !!this.editingId
    },
    canManage() {
      return this.userService.hasRight("d_manageAccidentProtocols")
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
  },
  methods: {
    formatFileSize(bytes) {
      if (!bytes) return "-"
      if (bytes < 1024) return bytes + " B"
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
      return (bytes / 1024 / 1024).toFixed(1) + " MB"
    },
    markAttachmentForRemoval(fileId) {
      this.form.removedAttachmentIds.push(fileId)
      this.existingAttachments = this.existingAttachments.filter((a) => a.fileId !== fileId)
    },
    async previewAttachment(att) {
      try {
        const url = await api.accidentReports.previewAttachment(this.currentReport._id, att.fileId)
        window.open(url, "_blank")
        // URL wird vom Browser nach dem Schliessen geräumt; alternativ setTimeout für revoke
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        console.error("Preview fehlgeschlagen:", e)
        alert("Vorschau konnte nicht geladen werden")
      }
    },
    downloadAttachmentFile(att) {
      api.accidentReports.downloadAttachment(this.currentReport._id, att.fileId, att.originalName)
    },
    async deleteAttachmentFile(att) {
      if (!confirm(`Anhang "${att.originalName}" wirklich löschen?`)) return
      try {
        await api.accidentReports.deleteAttachment(this.currentReport._id, att.fileId)
        // Detail neu laden
        this.currentReport = await api.accidentReports.fetchOne(this.currentReport._id)
        this.loadReports()
      } catch (e) {
        console.error("Löschen fehlgeschlagen:", e)
        alert("Anhang konnte nicht gelöscht werden")
      }
    },
    formatHistoryValue(val, fieldLabel) {
      if (val === null || val === undefined || val === "") return "-"
      if (typeof val === "boolean") return val ? "JA" : "NEIN"
      if (typeof val === "object" && val !== null) {
        if (val.name) return val.name
        if (val._id) return val._id
        return JSON.stringify(val)
      }
      if (typeof val === "string" && val.match(/^\d{4}-\d{2}-\d{2}/)) {
        const m = this.$moment(val)
        if (m.isValid()) {
          const dateOnlyFields = ["Geburtstag", "Eintrittsdatum"]
          return dateOnlyFields.includes(fieldLabel) ? m.format("DD.MM.YYYY") : m.format("DD.MM.YYYY HH:mm")
        }
      }
      return String(val)
    },
    canEdit(report) {
      if (!report || report.status === "done") return false
      // Manager dürfen immer bearbeiten (solange nicht erledigt)
      if (this.canManage) return true
      // Ersteller dürfen ihre eigene Meldung bearbeiten
      const creatorId = report.createdBy && report.createdBy._id ? report.createdBy._id : report.createdBy
      return creatorId && creatorId.toString() === this.userService.getUserId().toString()
    },
    openEditDialog(report) {
      // Form mit bestehenden Werten befüllen
      this.form = {
        studio: report.studio && report.studio._id ? report.studio._id : report.studio,
        firstName: report.firstName || "",
        lastName: report.lastName || "",
        address: report.address || "",
        phone: report.phone || "",
        email: report.email || "",
        birthday: report.birthday ? this.$moment(report.birthday).format("YYYY-MM-DD") : null,
        entryDate: report.entryDate ? this.$moment(report.entryDate).format("YYYY-MM-DD") : null,
        isWorkAccident: !!report.isWorkAccident,
        accidentDate: report.accidentDate ? this.$moment(report.accidentDate).format("YYYY-MM-DDTHH:mm") : null,
        accidentLocation: report.accidentLocation || "",
        accidentDescription: report.accidentDescription || "",
        hasPoliceReport: !!report.hasPoliceReport,
        injuredBodyPart: report.injuredBodyPart || "",
        bodySide: report.bodySide || "n/a",
        injuryType: report.injuryType || "",
        firstDoctor: report.firstDoctor || "",
        followUpDoctor: report.followUpDoctor || "",
        absenceMoreThan3Days: !!report.absenceMoreThan3Days,
        absenceMoreThan30Days: !!report.absenceMoreThan30Days,
        newAttachments: [],
        removedAttachmentIds: [],
      }
      this.editingId = report._id
      this.existingAttachments = [...(report.attachments || [])]
      this.detailDialog = false
      this.createDialog = true
    },
    getEmptyForm() {
      return {
        studio: this.canManage ? null : this.getSelectedStudioId,
        firstName: "",
        lastName: "",
        address: "",
        phone: "",
        email: "",
        birthday: null,
        entryDate: null,
        isWorkAccident: false,
        accidentDate: null,
        accidentLocation: "",
        accidentDescription: "",
        hasPoliceReport: false,
        injuredBodyPart: "",
        bodySide: "n/a",
        injuryType: "",
        firstDoctor: "",
        followUpDoctor: "",
        absenceMoreThan3Days: false,
        absenceMoreThan30Days: false,
        newAttachments: [], // NEU
        removedAttachmentIds: [], // NEU
      }
    },
    async loadReports() {
      this.loading = true
      try {
        const params = {}
        if (this.canManage && this.filterStudio && this.filterStudio !== "all") {
          params.studio = this.filterStudio
        }
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        this.reports = await api.accidentReports.fetch(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.form = this.getEmptyForm()
      this.editingId = null
      this.createDialog = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.resetValidation()
      })
    },
    closeCreateDialog() {
      this.createDialog = false
      this.editingId = null
    },
    async saveReport() {
      if (!this.$refs.createForm.validate()) return
      this.saving = true
      try {
        const fd = new FormData()
        const fields = [
          "studio",
          "firstName",
          "lastName",
          "address",
          "phone",
          "email",
          "birthday",
          "entryDate",
          "isWorkAccident",
          "accidentDate",
          "accidentLocation",
          "accidentDescription",
          "hasPoliceReport",
          "injuredBodyPart",
          "bodySide",
          "injuryType",
          "firstDoctor",
          "followUpDoctor",
          "absenceMoreThan3Days",
          "absenceMoreThan30Days",
        ]
        fields.forEach((f) => {
          const val = this.form[f]
          if (val !== null && val !== undefined) {
            fd.append(f, val)
          }
        })

        if (this.isEditMode && this.form.removedAttachmentIds.length > 0) {
          fd.append("removedAttachmentIds", this.form.removedAttachmentIds.join(","))
        }

        if (this.form.newAttachments && this.form.newAttachments.length > 0) {
          this.form.newAttachments.forEach((file) => fd.append("attachments", file))
        }

        if (this.isEditMode) {
          const updated = await api.accidentReports.update(this.editingId, fd)
          this.currentReport = updated
        } else {
          await api.accidentReports.create(fd)
        }

        this.createDialog = false
        this.editingId = null
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Speichern:", e)
        const msg = e.response?.data?.error || "Fehler beim Speichern der Unfallmeldung"
        alert(msg)
      } finally {
        this.saving = false
      }
    },
    async openDetailDialog(report) {
      try {
        this.currentReport = await api.accidentReports.fetchOne(report._id)
        this.newComment = ""
        this.detailDialog = true
      } catch (e) {
        console.error("Fehler beim Laden des Details:", e)
      }
    },
    async changeStatus(status) {
      if (!this.currentReport) return
      try {
        const updated = await api.accidentReports.updateStatus(this.currentReport._id, status)
        this.currentReport = updated
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Statuswechsel:", e)
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.currentReport) return
      this.savingComment = true
      try {
        const updated = await api.accidentReports.addComment(this.currentReport._id, this.newComment)
        this.currentReport = updated
        this.newComment = ""
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Kommentar:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteReport() {
      if (!this.currentReport) return
      if (!confirm("Unfallmeldung wirklich löschen?")) return
      try {
        await api.accidentReports.delete(this.currentReport._id)
        this.detailDialog = false
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
      }
    },
    downloadPdf(report) {
      const fileName = `Unfallmeldung_${report.lastName}_${this.$moment(report.accidentDate).format("YYYY-MM-DD")}.pdf`
      api.accidentReports.downloadPdf(report._id, fileName)
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    statusColor(s) {
      return s === "done" ? "green" : s === "in_progress" ? "orange" : "red"
    },
    statusLabel(s) {
      return s === "done" ? "Erledigt" : s === "in_progress" ? "In Bearbeitung" : "Offen"
    },
  },
  created() {
    this.loadReports()
  },
}
</script>

<style scoped>
.transparent {
  background: transparent !important;
}
</style>
